import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MapPin, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { STATE_LOCATIONS } from "../data/stateLocations";

const BASE = "https://www.peptidesformedspas.org";

const StatesIndexPage = () => {
  usePageSEO(
    "Med Spa Peptides by State | All 50 States + DC",
    "Research peptides studied in med spa contexts across all 50 U.S. states + DC. Each state page covers research categories plus state-specific med-spa regulatory context."
  );

  const states = Object.values(STATE_LOCATIONS);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${BASE}/locations#webpage`,
      url: `${BASE}/locations`,
      name: "Med Spa Peptides by State — Research Catalog Serving All 50 States",
      description:
        "Research peptides studied in med spa contexts across all 50 U.S. states + DC, with state-specific regulatory context.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${BASE}/locations#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Locations", item: `${BASE}/locations` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${BASE}/locations#itemlist`,
      name: "Research peptide resources by state",
      numberOfItems: states.length,
      itemListElement: states.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "WebPage",
          name: `Research Peptides for Med Spas in ${s.name}`,
          url: `${BASE}/locations/${s.slug}`,
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="states-index-page">
      <Header />
      <JsonLd id="states-index-jsonld" data={jsonLd} />

      <section className="pt-32 pb-10 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#a09186] mb-6">
            <Link to="/" className="hover:text-[#b89a6d]">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-[#3a2a26] font-medium">Locations</span>
          </nav>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#fdf2f4] border border-[#e8c5b8] rounded-full mb-4">
            <MapPin className="w-4 h-4 text-[#b89a6d]" />
            <span className="text-sm text-[#b89a6d] font-medium">Regional Research Guides</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6" data-testid="states-index-h1">
            Med Spa Peptide Research — by State
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed">
            A state-by-state educational index covering the peptide research categories most often referenced in med spa and aesthetic contexts, plus state-specific medical-practice and delegation rules. Not medical advice. PeptidesForMedSpas does not operate medical practices.
          </p>
        </div>
      </section>

      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="states-grid">
            {states.map((s) => (
              <Link
                key={s.slug}
                to={`/locations/${s.slug}`}
                data-testid={`state-link-${s.slug}`}
                className="bg-white border border-[#f0d8d4] hover:border-[#d4a5ad] rounded-xl p-4 transition-all group flex items-center justify-between"
              >
                <div>
                  <div className="text-xs text-[#a09186] font-semibold uppercase tracking-wider">{s.abbrev}</div>
                  <div className="text-sm font-bold text-[#3a2a26] group-hover:text-[#b89a6d] transition-colors">{s.name}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#b89a6d] opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6 text-center">
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              Each state page summarizes {`{state} regulatory context`} at a high level; it is not legal advice. Research peptides are sold for laboratory use only and are not approved by the FDA for human use. Consult a licensed clinician in your state before any wellness or aesthetic decision.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StatesIndexPage;
